import { serverURL } from "@/utils/serverURL";

// ── Wire types — mirror the server payload from
// apps/nos/server/src/controllers/me/flavor/formulas/versions/viz/
// Keep in lock-step when the server shape changes. ─────────────────────────

export type VizStatus = "pending" | "computing" | "done" | "cancelled" | "failed";

export type DrCoord = { x: number; y: number; z: number | null };

// Per-group hotspot detail for the radar drill-down. Keyed by receptor_group
// (the same keys present in `keys`). Each entry's `axes[i]` is an anonymized
// label like "S1", "S2" and aligns with `target[i]` / `current[i]`. Empty
// object when the server doesn't supply detail (smell in Phase 3).
export type RadarDetail = {
  axes: string[];
  target: number[];
  current: number[];
  /** Profile-vector indices feeding each detail axis. axes[i], target[i],
   *  and current[i] are all derived from profile[dimIdxs[i]]. Used by the
   *  AF apply phase to rebuild `current` from an in-flight m(S) so the
   *  Receptor-Detail radar moves with each AI step. */
  dimIdxs: number[];
};

export type AxesGroup = {
  keys: string[];
  displayNames: Record<string, string>;
  detail: Record<string, RadarDetail>;
};

export type VizPayload = {
  status: VizStatus;
  errorMessage: string | null;
  profile: number[] | null;
  drCoord: DrCoord | null;
  loss: number | null;
  tasteRadar: Record<string, number> | null;
  smellRadar: Record<string, number> | null;
  ingredientSort: Array<{ kind: "compound"; id: number; deltaLoss: number }> | null;
  target: {
    id: number;
    name: string | null;
    profile: number[];
    drCoord: DrCoord | null;
    tasteRadar: Record<string, number> | null;
  } | null;
  axes: { taste: AxesGroup; smell: AxesGroup };
  neighbors: Array<{
    id: number;
    name: string | null;
    /**
     * Phase 4: derived from platform.food_flavor_category. null when the
     * food isn't yet classified (AI worker hasn't picked it up).
     */
    family: { code: string; label: string; color: string } | null;
    drCoord: DrCoord;
    /** True for the inner 10 foods (within the top-100 backdrop) nearest to the
     *  reference point — these render full-opacity in the scatter. */
    isNearest: boolean;
    /** True for the inner 3 of those 10 — these get pinned text labels. */
    isLabeled: boolean;
    /**
     * T15: exact weighted L² loss from this food to the target.
     * Available for target_pca and taste_2d projections; null for kernel.
     * Used by ScatterPlot to compute iso-loss ring radii.
     */
    loss: number | null;
  }>;
};

// ── API error ──────────────────────────────────────────────────────────────

export class WorkbenchVizApiError extends Error {
  status: number;
  endpoint: string;
  constructor(status: number, endpoint: string) {
    super(`${endpoint} failed: ${status}`);
    this.name = "WorkbenchVizApiError";
    this.status = status;
    this.endpoint = endpoint;
  }
}

// ── Functions ──────────────────────────────────────────────────────────────

export async function getViz(
  flavorId: string,    // UUID
  formulaId: number,
  versionId: number,
): Promise<VizPayload> {
  const res = await fetch(
    serverURL`/me/flavor/${flavorId}/formulas/${formulaId}/versions/${versionId}/viz`,
    { credentials: "include" },
  );
  if (!res.ok) {
    throw new WorkbenchVizApiError(
      res.status,
      `flavor/${flavorId}/formulas/${formulaId}/versions/${versionId}/viz`,
    );
  }
  return (await res.json()) as VizPayload;
}

// ─── Phase 3: replacements ───────────────────────────────────────────────────

export type ReplacementSetItem = {
  itemNdbTable: string;
  itemRowid: number;
  positionInSet: number;
  name: string | null;
  cas: string | null;
};

export type ReplacementSet = {
  type: "single" | "multi";
  replacedLoss: number | null;
  replacementRank: number | null;
  replacementSize: number | null;
  items: ReplacementSetItem[];
  hasSupplier: boolean | null;
  safetySignal: "Danger" | "Warning" | null;
};

export type PerItemReplacements = {
  resultItemId: number;
  baseLoss: number | null;
  dropOnlyLoss: number | null;
  singles: ReplacementSet[];
  multis: ReplacementSet[];
};

export type ExtraItem = {
  itemNdbTable: string;
  itemRowid: number;
  positionInSet: number;
  concentration: number | null;
  unit: string | null;
};

export type ReplacementsPayload =
  | { status: "pending" }
  | {
      status: "ready";
      nblockResultId: number;
      perItem: Record<number, PerItemReplacements>;
      extraItems: ExtraItem[];
    };

// Note: GET /replacements has been removed. Replacements state is delivered
// exclusively through the SSE stream at /events/:vid — see
// useWorkbenchReplacements for the consumer pattern. The ReplacementsPayload
// type above stays exported for the SSE handler to parse incoming events.
